import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Highlighter, Type, RemoveFormatting,
} from 'lucide-react';

import './RichTextEditor.css';

const TEXT_COLORS = [
  { label: 'Défaut', value: null },
  { label: 'Bleu', value: '#3B82F6' },
  { label: 'Orange', value: '#f05a1a' },
  { label: 'Rouge', value: '#dc2626' },
  { label: 'Vert', value: '#16a34a' },
  { label: 'Gris', value: 'var(--text-muted)' },
  { label: 'Violet', value: '#7c3aed' },
];

const HIGHLIGHT_COLORS = [
  { label: 'Jaune', value: '#fef08a' },
  { label: 'Bleu clair', value: 'rgba(59,130,246,0.22)' },
  { label: 'Rose', value: '#fbcfe8' },
  { label: 'Vert clair', value: '#bbf7d0' },
  { label: 'Orange clair', value: '#fed7aa' },
];

function TBtn({ active, onClick, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      style={{
        width: 30, height: 30,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: active ? '1.5px solid #3B82F6' : '1px solid transparent',
        background: active ? 'rgba(59,130,246,0.08)' : 'transparent',
        borderRadius: 6, cursor: 'pointer',
        color: active ? '#3B82F6' : 'var(--text-primary)',
        transition: 'all 0.1s',
      }}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 3px', flexShrink: 0 }} />;
}

const RichTextEditor = React.forwardRef(function RichTextEditor({ onUpdate, placeholder = 'Rédigez votre rapport…', minHeight = 180, autoFocus = false, initialContent }, ref) {
  const [colorOpen, setColorOpen] = React.useState(false);
  const [hlOpen, setHlOpen] = React.useState(false);
  const [stats, setStats] = React.useState({ chars: 0, words: 0 });

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['paragraph'] }),
      Placeholder.configure({ placeholder, emptyEditorClass: 'rte-placeholder' }),
    ],
    content: initialContent || '',
    autofocus: autoFocus,
    onUpdate: ({ editor }) => {
      const text = editor.getText();
      const html = editor.getHTML();
      const words = text.trim() ? text.trim().split(/\s+/).length : 0;
      setStats({ chars: text.length, words });
      if (onUpdate) onUpdate({ html, text });
    },
  });

  React.useEffect(() => {
    if (!editor || !initialContent) return;
    const text = editor.getText();
    if (!text.trim()) {
      editor.commands.setContent(initialContent);
      const t = editor.getText();
      setStats({ chars: t.length, words: t.trim() ? t.trim().split(/\s+/).length : 0 });
      if (onUpdate) onUpdate({ html: editor.getHTML(), text: t });
    }
  }, [editor]);


  React.useEffect(() => {
    if (!editor) return;
    const closeDropdowns = () => { setColorOpen(false); setHlOpen(false); };
    document.addEventListener('mousedown', closeDropdowns);
    return () => document.removeEventListener('mousedown', closeDropdowns);
  }, [editor]);

  React.useImperativeHandle(ref, () => ({
    insertContent: (html) => {
      if (!editor) return;
      editor.chain().focus().insertContent(html).run();
    },
    setContent: (html) => {
      if (!editor) return;
      editor.commands.setContent(html || '');
    },
  }), [editor]);

  if (!editor) return null;

  return (
    <div className="rte-wrapper">
      <div className="rte-toolbar">
        <TBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Gras (Ctrl+B)">
          <Bold size={13} />
        </TBtn>
        <TBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italique (Ctrl+I)">
          <Italic size={13} />
        </TBtn>
        <TBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Souligné (Ctrl+U)">
          <UnderlineIcon size={13} />
        </TBtn>
        <TBtn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Barré">
          <Strikethrough size={13} />
        </TBtn>

        <Sep />

        {/* Text color */}
        <div style={{ position: 'relative' }} onMouseDown={(e) => e.stopPropagation()}>
          <TBtn active={colorOpen} onClick={() => { setColorOpen(v => !v); setHlOpen(false); }} title="Couleur du texte">
            <Type size={13} />
          </TBtn>
          {colorOpen && (
            <div className="rte-dropdown">
              <div className="rte-dropdown-label">Couleur du texte</div>
              <div className="rte-swatch-row">
                {TEXT_COLORS.map((c) => (
                  <button key={c.value || 'default'} type="button" title={c.label}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (c.value) editor.chain().focus().setColor(c.value).run();
                      else editor.chain().focus().unsetColor().run();
                      setColorOpen(false);
                    }}
                    style={{
                      width: 22, height: 22, borderRadius: '50%',
                      background: c.value || 'var(--bg-elevated)',
                      border: c.value ? '2px solid white' : '2px solid #d1d5db',
                      boxShadow: '0 0 0 1px #e5e7eb',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 9, color: 'var(--text-muted)',
                    }}>
                    {!c.value && '✕'}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Highlight */}
        <div style={{ position: 'relative' }} onMouseDown={(e) => e.stopPropagation()}>
          <TBtn active={hlOpen || editor.isActive('highlight')} onClick={() => { setHlOpen(v => !v); setColorOpen(false); }} title="Surligner">
            <Highlighter size={13} />
          </TBtn>
          {hlOpen && (
            <div className="rte-dropdown">
              <div className="rte-dropdown-label">Surligner</div>
              <div className="rte-swatch-row">
                {HIGHLIGHT_COLORS.map((h) => (
                  <button key={h.value} type="button" title={h.label}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.chain().focus().toggleHighlight({ color: h.value }).run();
                      setHlOpen(false);
                    }}
                    style={{ width: 22, height: 22, borderRadius: 5, background: h.value, border: '2px solid white', boxShadow: '0 0 0 1px #e5e7eb', cursor: 'pointer' }} />
                ))}
                <button type="button" title="Effacer"
                  onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().unsetHighlight().run(); setHlOpen(false); }}
                  style={{ width: 22, height: 22, borderRadius: 5, background: 'var(--bg-card)', border: '2px solid #d1d5db', cursor: 'pointer', fontSize: 9, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
              </div>
            </div>
          )}
        </div>

        <Sep />

        <TBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Liste à puces">
          <List size={13} />
        </TBtn>
        <TBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Liste numérotée">
          <ListOrdered size={13} />
        </TBtn>

        <Sep />

        <TBtn active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Aligner à gauche">
          <AlignLeft size={13} />
        </TBtn>
        <TBtn active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Centrer">
          <AlignCenter size={13} />
        </TBtn>
        <TBtn active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Aligner à droite">
          <AlignRight size={13} />
        </TBtn>

        <Sep />

        <TBtn active={false} onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Effacer toute la mise en forme">
          <RemoveFormatting size={13} />
        </TBtn>

        <div style={{ marginLeft: 'auto', fontSize: '0.6875rem', color: '#94a3b8', display: 'flex', gap: 8, alignItems: 'center', paddingRight: 4 }}>
          <span>{stats.words} mot{stats.words !== 1 ? 's' : ''}</span>
          <span style={{ color: '#e2e8f0' }}>|</span>
          <span>{stats.chars} car.</span>
        </div>
      </div>

      <EditorContent editor={editor} className="rte-content" style={{ minHeight }} />
    </div>
  );
});

export default RichTextEditor;
